import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { PackageOpen, Plus, Trash2, CheckCircle2 } from "lucide-react";

export default function GRN() {
  const [grns, setGrns] = useState([]);
  const [items, setItems] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ warehouse_id: "", vendor_name: "", notes: "", lines: [] });

  const load = async () => {
    const [g, it, wh] = await Promise.all([
      api.get("/warehouse/grns"),
      api.get("/inventory/items"),
      api.get("/inventory/warehouses"),
    ]);
    setGrns(g.data); setItems(it.data); setWarehouses(wh.data);
  };
  useEffect(() => { load(); }, []);

  const addLine = () => setForm((f) => ({ ...f, lines: [...f.lines, { item_id: "", qty_received: 0, unit_cost: 0, batch_number: "", qc_status: "PENDING" }] }));
  const removeLine = (i) => setForm((f) => ({ ...f, lines: f.lines.filter((_, idx) => idx !== i) }));
  const updateLine = (i, patch) => setForm((f) => ({ ...f, lines: f.lines.map((l, idx) => idx === i ? { ...l, ...patch } : l) }));

  const save = async () => {
    if (!form.warehouse_id || form.lines.length === 0) {
      toast.error("Pick a warehouse and add at least one line"); return;
    }
    try {
      await api.post("/warehouse/grns", form);
      toast.success("GRN created (DRAFT)");
      setOpen(false); setForm({ warehouse_id: "", vendor_name: "", notes: "", lines: [] }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const postGrn = async (id) => {
    try { await api.post(`/warehouse/grns/${id}/post`); toast.success("GRN posted to stock"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid="warehouse-grn">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-black flex items-center gap-2">
            <PackageOpen className="w-7 h-7 text-primary" /> Goods Receipt Notes
          </h1>
          <p className="text-muted-foreground">Receive against POs, capture QC status, post to stock.</p>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="grn-new"><Plus className="w-4 h-4 mr-1" /> New GRN</Button>
      </div>

      <div className="surface-card p-4 overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Number</TableHead><TableHead>Vendor</TableHead><TableHead>Warehouse</TableHead>
            <TableHead className="text-right">Lines</TableHead><TableHead>Status</TableHead>
            <TableHead>Received</TableHead><TableHead className="text-right">Action</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {grns.map((g) => (
              <TableRow key={g.id} data-testid={`grn-row-${g.number}`}>
                <TableCell className="font-mono text-xs">{g.number}</TableCell>
                <TableCell>{g.vendor_name || "—"}</TableCell>
                <TableCell>{g.warehouse_name}</TableCell>
                <TableCell className="text-right">{g.lines?.length ?? 0}</TableCell>
                <TableCell>
                  <Badge variant={g.status === "POSTED" ? "default" : "outline"}>{g.status}</Badge>
                </TableCell>
                <TableCell className="text-xs font-mono">{(g.received_date || "").slice(0, 10)}</TableCell>
                <TableCell className="text-right">
                  {g.status === "DRAFT" && (
                    <Button size="sm" variant="outline" onClick={() => postGrn(g.id)} data-testid={`grn-post-${g.number}`}>
                      <CheckCircle2 className="w-4 h-4 mr-1" /> Post
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {grns.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-10">No GRNs yet.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>New goods receipt note</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Warehouse</Label>
                <Select value={form.warehouse_id} onValueChange={(v) => setForm({ ...form, warehouse_id: v })}>
                  <SelectTrigger data-testid="grn-wh"><SelectValue placeholder="Pick warehouse…" /></SelectTrigger>
                  <SelectContent>
                    {warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Vendor name</Label>
                <Input value={form.vendor_name} onChange={(e) => setForm({ ...form, vendor_name: e.target.value })} data-testid="grn-vendor" />
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>

            <div className="flex items-center justify-between pt-2">
              <Label>Lines</Label>
              <Button variant="outline" size="sm" onClick={addLine} data-testid="grn-add-line"><Plus className="w-4 h-4 mr-1" /> Add line</Button>
            </div>
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {form.lines.map((ln, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-end">
                  <div className="col-span-4">
                    <Label className="text-[10px]">Item</Label>
                    <Select value={ln.item_id} onValueChange={(v) => updateLine(i, { item_id: v })}>
                      <SelectTrigger data-testid={`grn-line-item-${i}`}><SelectValue placeholder="Item…" /></SelectTrigger>
                      <SelectContent>
                        {items.map((it) => <SelectItem key={it.id} value={it.id}>{it.sku} · {it.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2">
                    <Label className="text-[10px]">Qty</Label>
                    <Input type="number" value={ln.qty_received} onChange={(e) => updateLine(i, { qty_received: parseFloat(e.target.value || "0") })} data-testid={`grn-line-qty-${i}`} />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-[10px]">Unit cost</Label>
                    <Input type="number" value={ln.unit_cost} onChange={(e) => updateLine(i, { unit_cost: parseFloat(e.target.value || "0") })} />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-[10px]">Batch</Label>
                    <Input value={ln.batch_number} onChange={(e) => updateLine(i, { batch_number: e.target.value })} />
                  </div>
                  <div className="col-span-1">
                    <Label className="text-[10px]">QC</Label>
                    <Select value={ln.qc_status} onValueChange={(v) => updateLine(i, { qc_status: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="PENDING">Pending</SelectItem>
                        <SelectItem value="PASS">Pass</SelectItem>
                        <SelectItem value="FAIL">Fail</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-1">
                    <Button variant="ghost" size="sm" onClick={() => removeLine(i)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                  </div>
                </div>
              ))}
              {form.lines.length === 0 && <div className="text-sm text-muted-foreground text-center py-4">No lines. Add one above.</div>}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="grn-save">Save as draft</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
